import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Check, X, RefreshCw, TrendingUp, TrendingDown, Users, Shield, Key, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Cell } from 'recharts';

// Local image paths matching other pages
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

// Package configuration with colors matching PageFive.jsx
const packageConfig = [
  {
    id: 'bronze',
    name: 'BRONZE',
    subtitle: 'The Essentials',
    color: 'from-amber-700 to-amber-900',
    borderColor: 'border-amber-700',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-900',
    headerBg: 'bg-gradient-to-r from-amber-700 to-amber-900'
  },
  {
    id: 'silver',
    name: 'SILVER',
    subtitle: 'The Commuter',
    color: 'from-gray-400 to-gray-600',
    borderColor: 'border-gray-500',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    headerBg: 'bg-gradient-to-r from-gray-400 to-gray-600'
  },
  {
    id: 'gold',
    name: 'GOLD',
    subtitle: 'The Remote Pro',
    color: 'from-yellow-500 to-yellow-700',
    borderColor: 'border-yellow-600',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-800',
    headerBg: 'bg-gradient-to-r from-yellow-500 to-yellow-700'
  },
  {
    id: 'platinum',
    name: 'PLATINUM',
    subtitle: 'The Entrepreneur',
    color: 'from-slate-600 to-slate-800',
    borderColor: 'border-slate-600',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-800',
    headerBg: 'bg-gradient-to-r from-slate-600 to-slate-800'
  }
];

const feeTypes = [
  { id: 'initiationFee', label: 'Initiation Fee', type: 'currency' },
  { id: 'submissionFee', label: 'Submission Fee', type: 'currency' },
  { id: 'expressFee', label: 'Express Fee', type: 'currency' },
  { id: 'refundFee', label: 'Refund Fee', type: 'percentage' }
];

const surchargeTypes = [
  { id: 'cardFee', label: 'Card Processing Fee', description: 'Added to card payments' },
  { id: 'debitOrderFee', label: 'Debit Order Service Fee', description: 'Added to debit order payments' },
  { id: 'debitOrderDiscount', label: 'Debit Order Full Payment Discount', description: 'Discount for paying full amount via debit order' },
  { id: 'laybyFee', label: 'Lay-by Service Fee', description: 'Added to lay-by payments' }
];

// Agreement fees configuration for Page 04 and Page 05
const agreementFeeTypes = [
  { id: 'minInitiationFee', label: 'Minimum Initiation Fee', type: 'currency', section: 'Page 04 - 2.1' },
  { id: 'minSubmissionFee', label: 'Minimum Submission Fee', type: 'currency', section: 'Page 04 - 2.2' },
  { id: 'dailyLatePenalty', label: 'Daily Late Payment Penalty', type: 'currency', section: 'Page 04 - 2.4' },
  { id: 'maxLatePenaltyDays', label: 'Maximum Late Payment Penalty (days)', type: 'number', section: 'Page 04 - 2.5' },
  { id: 'terminationFee', label: 'Termination Fee', type: 'currency', section: 'Page 04 - 4.3' },
  { id: 'disputeAppealFee', label: 'Dispute/Appeal Fee', type: 'currency', section: 'Page 05 - Clause 2' },
  { id: 'taxConsultationFee', label: 'Tax Consultation Fee', type: 'currency', section: 'Page 05 - Clause 3' },
  { id: 'efilingActivationFee', label: 'eFiling Activation Fee', type: 'currency', section: 'Page 05 - Clause 4' }
];

const AdminPage = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  
  // Audit Logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogDays, setAuditLogDays] = useState(7);
  
  // Pending Signups state
  const [pendingSignups, setPendingSignups] = useState([]);
  const [isLoadingSignups, setIsLoadingSignups] = useState(false);
  const [showPendingSignups, setShowPendingSignups] = useState(false);
  const [processingSignup, setProcessingSignup] = useState(null);
  
  // Activity Dashboard state
  const [activityStats, setActivityStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [showActivityDashboard, setShowActivityDashboard] = useState(true);
  const [statsPeriod, setStatsPeriod] = useState(7);

  // Email Delivery Logs state
  const [emailLogs, setEmailLogs] = useState([]);
  const [isLoadingEmailLogs, setIsLoadingEmailLogs] = useState(false);
  const [showEmailLogs, setShowEmailLogs] = useState(false);
  const [emailLogDays, setEmailLogDays] = useState(30);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch current pricing and admin user info on mount
  useEffect(() => {
    fetchPricing();
    fetchAdminUser();
  }, []);

  const fetchAdminUser = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (token) {
        const response = await fetch(`${BACKEND_URL}/api/admin/check-auth`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAdminUser(data.user);
        }
      }
    } catch (error) {
      console.error('Error fetching admin user:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  // Fetch Audit Logs (super admin only)
  const fetchAuditLogs = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token || adminUser?.role !== 'super_admin') return;
    
    setIsLoadingLogs(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/audit-logs?days=${auditLogDays}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
      } else if (response.status === 403) {
        toast.error('Access denied: Super admin only');
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Fetch Email Delivery Logs (super admin only)
  const fetchEmailLogs = async (days = emailLogDays) => {
    const token = localStorage.getItem('admin_token');
    if (!token || adminUser?.role !== 'super_admin') return;

    setIsLoadingEmailLogs(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-delivery-logs?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEmailLogs(data.logs || []);
      } else if (response.status === 403) {
        toast.error('Access denied: Super admin only');
      }
    } catch (error) {
      console.error('Error fetching email delivery logs:', error);
      toast.error('Failed to load email delivery logs');
    } finally {
      setIsLoadingEmailLogs(false);
    }
  };

  // Fetch Pending Signups (super admin only)
  const fetchPendingSignups = async () => {    const token = localStorage.getItem('admin_token');
    if (!token || adminUser?.role !== 'super_admin') return;
    
    setIsLoadingSignups(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pending-signups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingSignups(data.pending_signups || []);
      } else if (response.status === 403) {
        toast.error('Access denied: Super admin only');
      }
    } catch (error) {
      console.error('Error fetching pending signups:', error);
      toast.error('Failed to load pending signups');
    } finally {
      setIsLoadingSignups(false);
    }
  };

  // Approve or Reject Signup
  const handleSignupAction = async (username, approved) => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    
    setProcessingSignup(username);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/approve-signup`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, approved })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(approved ? `Approved access for ${username}` : `Rejected access for ${username}`);
        fetchPendingSignups(); // Refresh list
      } else {
        throw new Error(data.detail || 'Action failed');
      }
    } catch (error) {
      console.error('Error processing signup:', error);
      toast.error(error.message);
    } finally {
      setProcessingSignup(null);
    }
  };

  // Format timestamp for audit logs
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-ZA', { 
      dateStyle: 'medium', 
      timeStyle: 'short' 
    });
  };

  // Fetch Activity Stats (super admin only)
  const fetchActivityStats = async (period = statsPeriod) => {
    const token = localStorage.getItem('admin_token');
    if (!token || adminUser?.role !== 'super_admin') return;
    
    setIsLoadingStats(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/activity-stats?days=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActivityStats(data);
      } else if (response.status === 403) {
        toast.error('Access denied: Super admin only');
      }
    } catch (error) {
      console.error('Error fetching activity stats:', error);
      toast.error('Failed to load activity statistics');
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Load activity stats when admin user is loaded
  useEffect(() => {
    if (adminUser?.role === 'super_admin') {
      fetchActivityStats();
    }
  }, [adminUser]);

  const fetchPricing = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/pricing`);
      const data = await response.json();
      
      setPricing(data);
      if (data.updatedAt) {
        setLastUpdated(new Date(data.updatedAt));
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error('Failed to load pricing configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePackageFeeChange = (packageId, feeType, value) => {
    // Remove non-numeric characters except for empty string
    const numericValue = value.replace(/[^0-9]/g, '');
    const numValue = numericValue === '' ? 0 : parseInt(numericValue, 10);
    
    setPricing(prev => ({
      ...prev,
      packages: {
        ...prev.packages,
        [packageId]: {
          ...prev.packages[packageId],
          [feeType]: numValue
        }
      }
    }));
  };

  const handleSurchargeChange = (surchargeId, value) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const numValue = numericValue === '' ? 0 : parseInt(numericValue, 10);
    
    setPricing(prev => ({
      ...prev,
      paymentSurcharges: {
        ...prev.paymentSurcharges,
        [surchargeId]: numValue
      }
    }));
  };

  const handleAgreementFeeChange = (feeId, value) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const numValue = numericValue === '' ? 0 : parseInt(numericValue, 10);
    
    setPricing(prev => ({
      ...prev,
      agreementFees: {
        ...prev.agreementFees,
        [feeId]: numValue
      }
    }));
  };

  // Format currency with thousand separators (for display in inputs, no decimals)
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Format currency with 2 decimal places and thousand separators
  const formatCurrencyFull = (value) => {
    if (value === undefined || value === null) return '0.00';
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Format percentage with 2 decimal places
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '0.00';
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculate max late payment penalty (dynamic: days × daily penalty)
  const calculateMaxLatePenalty = () => {
    if (!pricing?.agreementFees) return 0;
    const days = pricing.agreementFees.maxLatePenaltyDays || 0;
    const dailyPenalty = pricing.agreementFees.dailyLatePenalty || 0;
    return days * dailyPenalty;
  };

  // Calculate discounted debit order fee
  const calculateDiscountedDebitFee = () => {
    if (!pricing?.paymentSurcharges) return 0;
    const fee = pricing.paymentSurcharges.debitOrderFee || 0;
    const discount = pricing.paymentSurcharges.debitOrderDiscount || 0;
    return fee - discount;
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Saving prices requires the admin login token (the backend rejects it otherwise)
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          packages: pricing.packages,
          paymentSurcharges: pricing.paymentSurcharges,
          agreementFees: pricing.agreementFees
        })
      });

      if (response.status === 401 || response.status === 403) {
        // Session expired (2h) or not logged in. Keep the edits on screen so nothing is lost:
        // logging in again in another tab refreshes the token, then Save works.
        toast.error('Your admin session has expired. Your changes are still on this page. Log in again in a new tab, then click Save again.');
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success('Pricing configuration saved successfully!');
        setLastUpdated(new Date(data.updatedAt));
      } else {
        throw new Error(data.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error('Failed to save pricing configuration');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-tca-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top Bar with user info and logout */}
      <div className="bg-tca-blue text-white px-4 py-2 flex justify-between items-center">
        <span className="text-sm">
          {adminUser ? `Logged in as: ${adminUser.username}` : 'Admin Dashboard'}
        </span>
        <Button
          onClick={handleLogout}
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/20"
          data-testid="admin-logout-btn"
        >
          Logout
        </Button>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-6 sm:pb-8 bg-white">
        {/* Logo Section */}
        <div className="mb-4 sm:mb-6 flex flex-col items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="TCA Auditors Logo" 
            className="h-16 sm:h-20 w-auto object-contain"
          />
          <img 
            src={BANNER_URL} 
            alt="Tax & Auditing Consultants" 
            className="h-10 sm:h-14 w-auto object-contain"
          />
        </div>

        {/* Main Heading */}
        <div className="mb-6 pb-2">
          <h1 className="text-xl sm:text-2xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            ADMIN DASHBOARD
          </h1>
        </div>

        {/* Admin Card */}
        <Card className="w-full max-w-6xl border shadow-card">
          <CardContent className="p-4 sm:p-8">
            {/* Document Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 underline">PRICING CONFIGURATION</h2>
              <p className="text-gray-600 mt-2">Manage agreement fees, package fees, and payment method surcharges</p>
              {lastUpdated && (
                <p className="text-sm text-gray-500 mt-1">
                  Last updated: {lastUpdated.toLocaleString('en-ZA', { 
                    dateStyle: 'medium', 
                    timeStyle: 'short' 
                  })}
                </p>
              )}
            </div>

            {/* Agreement Fees Table - NEW */}
            <div className="mb-8">
              <h3 className="font-bold text-lg underline mb-4 text-gray-900">AGREEMENT FEES</h3>
              <p className="text-sm text-gray-500 mb-4">These fees appear in the Tax Services Master Engagement Letter (Page 04)</p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse max-w-2xl">
                  {/* Header Row */}
                  <thead>
                    <tr>
                      <th className="p-3 text-left bg-tca-blue text-white font-bold border border-gray-300 w-1/2">
                        Fee Type
                      </th>
                      <th className="p-3 text-center bg-tca-blue text-white font-bold border border-gray-300 w-1/4">
                        Section
                      </th>
                      <th className="p-3 text-center bg-tca-blue text-white font-bold border border-gray-300 w-1/4">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Body Rows */}
                  <tbody>
                    {agreementFeeTypes.map((fee, rowIndex) => (
                      <tr key={fee.id} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-3 font-semibold text-gray-700 border border-gray-300">
                          {fee.label}
                        </td>
                        <td className="p-3 text-center text-gray-600 border border-gray-300">
                          {fee.section}
                        </td>
                        <td className="p-2 border border-gray-300">
                          <div className="relative">
                            {fee.type === 'currency' && (
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                                R
                              </span>
                            )}
                            <input
                              type="text"
                              value={fee.type === 'currency' 
                                ? formatCurrencyFull(pricing?.agreementFees?.[fee.id] || 0)
                                : fee.type === 'percentage'
                                  ? formatPercentage(pricing?.agreementFees?.[fee.id] || 0)
                                  : formatCurrencyFull(pricing?.agreementFees?.[fee.id] || 0)}
                              onChange={(e) => handleAgreementFeeChange(fee.id, e.target.value)}
                              className={`w-full p-2 ${fee.type === 'currency' ? 'pl-7' : 'pl-3'} ${fee.type === 'percentage' ? 'pr-7' : 'pr-3'} border-2 rounded-lg font-bold text-right border-tca-blue text-tca-blue bg-white focus:outline-none focus:ring-2 focus:ring-tca-blue`}
                            />
                            {fee.type === 'percentage' && (
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                                %
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Calculated Row: Maximum Late Payment */}
                    <tr className="bg-blue-50">
                      <td className="p-3 font-semibold text-gray-700 border border-gray-300">
                        Maximum Late Payment Penalty
                        <span className="text-xs text-gray-500 block">(Calculated: Days × Daily Penalty)</span>
                      </td>
                      <td className="p-3 text-center text-gray-600 border border-gray-300">
                        Page 04 - 2.5
                      </td>
                      <td className="p-3 text-right border border-gray-300">
                        <span className="font-bold text-tca-blue text-lg">
                          R{formatCurrencyFull(calculateMaxLatePenalty())}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-300 mb-8"></div>

            {/* Package Pricing Table */}
            <div className="mb-8">
              <h3 className="font-bold text-lg underline mb-4 text-gray-900">PACKAGE FEES</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  {/* Header Row */}
                  <thead>
                    <tr>
                      <th className="p-3 text-left bg-tca-blue text-white font-bold border border-gray-300 w-1/5">
                        Fee Type
                      </th>
                      {packageConfig.map(pkg => (
                        <th 
                          key={pkg.id}
                          className={`p-3 text-center text-white font-bold border border-gray-300 w-1/5 ${pkg.headerBg}`}
                        >
                          <div>{pkg.name}</div>
                          <div className="text-xs font-normal opacity-90">{pkg.subtitle}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  
                  {/* Body Rows */}
                  <tbody>
                    {feeTypes.map((fee, rowIndex) => (
                      <tr key={fee.id} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-3 font-semibold text-gray-700 border border-gray-300">
                          {fee.label}
                          {fee.type === 'percentage' && (
                            <span className="text-xs text-gray-500 ml-1">(%)</span>
                          )}
                        </td>
                        {packageConfig.map(pkg => (
                          <td 
                            key={`${pkg.id}-${fee.id}`}
                            className={`p-2 border border-gray-300 ${pkg.bgColor}`}
                          >
                            <div className="relative">
                              {fee.type === 'currency' && (
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                                  R
                                </span>
                              )}
                              <input
                                type="text"
                                value={fee.type === 'currency' 
                                  ? formatCurrencyFull(pricing?.packages?.[pkg.id]?.[fee.id] || 0)
                                  : formatPercentage(pricing?.packages?.[pkg.id]?.[fee.id] || 0)}
                                onChange={(e) => handlePackageFeeChange(pkg.id, fee.id, e.target.value)}
                                className={`w-full p-2 ${fee.type === 'currency' ? 'pl-7' : 'pl-3'} ${fee.type === 'percentage' ? 'pr-7' : 'pr-3'} border-2 rounded-lg font-bold text-right ${pkg.borderColor} ${pkg.textColor} bg-white focus:outline-none focus:ring-2 focus:ring-tca-blue`}
                              />
                              {fee.type === 'percentage' && (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                                  %
                                </span>
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-300 mb-8"></div>

            {/* Payment Surcharges Table */}
            <div className="mb-8">
              <h3 className="font-bold text-lg underline mb-4 text-gray-900">PAYMENT METHOD SURCHARGES</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse max-w-2xl">
                  {/* Header Row */}
                  <thead>
                    <tr>
                      <th className="p-3 text-left bg-tca-blue text-white font-bold border border-gray-300 w-1/2">
                        Payment Method Fee
                      </th>
                      <th className="p-3 text-center bg-tca-blue text-white font-bold border border-gray-300 w-1/2">
                        Amount (R)
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Body Rows */}
                  <tbody>
                    {surchargeTypes.map((surcharge, rowIndex) => (
                      <tr key={surcharge.id} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-3 border border-gray-300">
                          <div className="font-semibold text-gray-700">{surcharge.label}</div>
                          <div className="text-xs text-gray-500">{surcharge.description}</div>
                        </td>
                        <td className="p-2 border border-gray-300">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                              R
                            </span>
                            <input
                              type="text"
                              value={formatCurrencyFull(pricing?.paymentSurcharges?.[surcharge.id] || 0)}
                              onChange={(e) => handleSurchargeChange(surcharge.id, e.target.value)}
                              className="w-full p-2 pl-7 pr-3 border-2 rounded-lg font-bold text-right border-tca-blue text-tca-blue bg-white focus:outline-none focus:ring-2 focus:ring-tca-blue"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Calculated Row: Discounted Debit Order Fee */}
                    <tr className="bg-blue-50">
                      <td className="p-3 border border-gray-300">
                        <div className="font-semibold text-gray-700">Discounted Debit Order Fee</div>
                        <div className="text-xs text-gray-500">(Calculated: Debit Order Fee - Discount)</div>
                      </td>
                      <td className="p-3 text-right border border-gray-300">
                        <span className="font-bold text-tca-blue text-lg">
                          R{formatCurrencyFull(calculateDiscountedDebitFee())}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-center mt-8 pt-6 border-t">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-12 h-14 text-lg rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Saving...
                  </>
                ) : (
                  '💾 Save Pricing Configuration'
                )}
              </Button>
            </div>

            {/* Super Admin Section - Only visible to super_admin */}
            {adminUser?.role === 'super_admin' && (
              <>
                {/* Separator */}
                <div className="border-t border-gray-300 mt-8 mb-6"></div>
                
                <h3 className="font-bold text-xl text-center text-gray-900 mb-6">🔐 Super Admin Tools</h3>

                {/* Activity Dashboard */}
                <div className="mb-6">
                  <button
                    onClick={() => setShowActivityDashboard(!showActivityDashboard)}
                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-tca-blue to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md"
                  >
                    <span className="font-semibold flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Activity Dashboard
                    </span>
                    <div className="flex items-center gap-2">
                      {activityStats?.summary?.total_events > 0 && (
                        <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full">
                          {activityStats.summary.total_events} events
                        </span>
                      )}
                      {showActivityDashboard ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </button>

                  {showActivityDashboard && (
                    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-white">
                      {/* Header with period selector */}
                      <div className="p-4 bg-gray-50 border-b flex justify-between items-center flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <label className="text-sm text-gray-600 font-medium">Period:</label>
                          <select
                            value={statsPeriod}
                            onChange={(e) => {
                              const period = parseInt(e.target.value);
                              setStatsPeriod(period);
                              fetchActivityStats(period);
                            }}
                            className="text-sm border rounded-md px-3 py-1.5 bg-white focus:ring-2 focus:ring-tca-blue"
                          >
                            <option value={1}>Last 24 hours</option>
                            <option value={7}>Last 7 days</option>
                            <option value={14}>Last 14 days</option>
                            <option value={30}>Last 30 days</option>
                          </select>
                        </div>
                        <Button
                          onClick={() => fetchActivityStats()}
                          disabled={isLoadingStats}
                          variant="outline"
                          size="sm"
                          className="h-8"
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${isLoadingStats ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>

                      {isLoadingStats ? (
                        <div className="p-8 text-center text-gray-500">
                          <div className="w-8 h-8 border-3 border-gray-300 border-t-tca-blue rounded-full animate-spin mx-auto mb-3"></div>
                          Loading activity statistics...
                        </div>
                      ) : activityStats ? (
                        <div className="p-4 space-y-6">
                          {/* Summary Cards */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Successful Logins</p>
                                  <p className="text-2xl font-bold text-green-700 mt-1">{activityStats.summary.successful_logins}</p>
                                </div>
                                <div className="p-2 bg-green-200 rounded-lg">
                                  <Check className="h-5 w-5 text-green-700" />
                                </div>
                              </div>
                              <div className="mt-2 flex items-center text-xs text-green-600">
                                <TrendingUp className="h-3 w-3 mr-1" />
                                {activityStats.summary.success_rate}% success rate
                              </div>
                            </div>

                            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs text-red-600 font-medium uppercase tracking-wide">Failed Attempts</p>
                                  <p className="text-2xl font-bold text-red-700 mt-1">{activityStats.summary.failed_logins}</p>
                                </div>
                                <div className="p-2 bg-red-200 rounded-lg">
                                  <X className="h-5 w-5 text-red-700" />
                                </div>
                              </div>
                              {activityStats.summary.failed_logins > 5 && (
                                <div className="mt-2 flex items-center text-xs text-red-600">
                                  <TrendingDown className="h-3 w-3 mr-1" />
                                  Monitor for suspicious activity
                                </div>
                              )}
                            </div>

                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Unique Users</p>
                                  <p className="text-2xl font-bold text-blue-700 mt-1">{activityStats.summary.unique_users}</p>
                                </div>
                                <div className="p-2 bg-blue-200 rounded-lg">
                                  <Users className="h-5 w-5 text-blue-700" />
                                </div>
                              </div>
                            </div>

                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">Password Resets</p>
                                  <p className="text-2xl font-bold text-purple-700 mt-1">{activityStats.summary.password_resets}</p>
                                </div>
                                <div className="p-2 bg-purple-200 rounded-lg">
                                  <Key className="h-5 w-5 text-purple-700" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Daily Activity Chart */}
                          <div className="bg-gray-50 rounded-xl p-4 border">
                            <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                              <Activity className="h-4 w-4" />
                              Daily Activity
                            </h4>
                            <div className="h-48">
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={activityStats.daily_activity} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                  <defs>
                                    <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="failedGradient" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                                  <Tooltip 
                                    contentStyle={{ 
                                      backgroundColor: 'white', 
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '8px',
                                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                                    }}
                                  />
                                  <Area type="monotone" dataKey="success" name="Successful" stroke="#22c55e" fill="url(#successGradient)" strokeWidth={2} />
                                  <Area type="monotone" dataKey="failed" name="Failed" stroke="#ef4444" fill="url(#failedGradient)" strokeWidth={2} />
                                </AreaChart>
                              </ResponsiveContainer>
                            </div>
                          </div>

                          {/* Hourly Distribution Chart */}
                          <div className="bg-gray-50 rounded-xl p-4 border">
                            <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                              <Shield className="h-4 w-4" />
                              Activity by Hour (UTC)
                            </h4>
                            <div className="h-32">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={activityStats.hourly_activity} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                                  <XAxis 
                                    dataKey="hour" 
                                    tick={{ fontSize: 10 }} 
                                    stroke="#9ca3af"
                                    interval={2}
                                  />
                                  <YAxis hide />
                                  <Tooltip 
                                    formatter={(value) => [`${value} events`, 'Count']}
                                    labelFormatter={(hour) => `${hour}:00`}
                                    contentStyle={{ 
                                      backgroundColor: 'white', 
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '8px',
                                      fontSize: '12px'
                                    }}
                                  />
                                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {activityStats.hourly_activity.map((entry, index) => (
                                      <Cell 
                                        key={`cell-${index}`} 
                                        fill={entry.count > 0 ? '#1e3a5f' : '#e5e7eb'} 
                                      />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>

                          {/* Recent Activity */}
                          {activityStats.recent_activity?.length > 0 && (
                            <div className="bg-gray-50 rounded-xl p-4 border">
                              <h4 className="font-semibold text-gray-700 mb-3">Recent Activity</h4>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {activityStats.recent_activity.slice(0, 5).map((activity, idx) => (
                                  <div key={idx} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border text-sm">
                                    <div className="flex items-center gap-3">
                                      <span className={`w-2 h-2 rounded-full ${activity.success ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                      <span className="font-medium text-gray-700">{activity.action?.replace(/_/g, ' ')}</span>
                                    </div>
                                    <div className="flex items-center gap-4 text-gray-500">
                                      <span className="text-xs">{activity.username || 'N/A'}</span>
                                      <span className="text-xs">{formatTimestamp(activity.timestamp)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          No activity data available
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Pending Signups Section */}
                <div className="mb-6">
                  <button
                    onClick={() => {
                      setShowPendingSignups(!showPendingSignups);
                      if (!showPendingSignups && pendingSignups.length === 0) {
                        fetchPendingSignups();
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    <span className="font-semibold text-amber-800">📋 Pending Signup Requests</span>
                    <div className="flex items-center gap-2">
                      {pendingSignups.length > 0 && (
                        <span className="bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                          {pendingSignups.length}
                        </span>
                      )}
                      {showPendingSignups ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </button>
                  
                  {showPendingSignups && (
                    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-3 bg-gray-50 border-b flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          {pendingSignups.length} pending request(s)
                        </span>
                        <Button
                          onClick={fetchPendingSignups}
                          disabled={isLoadingSignups}
                          variant="outline"
                          size="sm"
                          className="h-8"
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${isLoadingSignups ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                      
                      {isLoadingSignups ? (
                        <div className="p-6 text-center text-gray-500">
                          <div className="w-6 h-6 border-2 border-gray-300 border-t-tca-blue rounded-full animate-spin mx-auto mb-2"></div>
                          Loading...
                        </div>
                      ) : pendingSignups.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          No pending signup requests
                        </div>
                      ) : (
                        <div className="divide-y">
                          {pendingSignups.map((signup) => (
                            <div key={signup.username} className="p-4 flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{signup.username}</p>
                                <p className="text-xs text-gray-500">
                                  Requested: {formatTimestamp(signup.requested_at)}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleSignupAction(signup.username, true)}
                                  disabled={processingSignup === signup.username}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white h-8"
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  onClick={() => handleSignupAction(signup.username, false)}
                                  disabled={processingSignup === signup.username}
                                  variant="outline"
                                  size="sm"
                                  className="border-red-300 text-red-600 hover:bg-red-50 h-8"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Audit Logs Section */}
                <div className="mb-6">
                  <button
                    onClick={() => {
                      setShowAuditLogs(!showAuditLogs);
                      if (!showAuditLogs && auditLogs.length === 0) {
                        fetchAuditLogs();
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <span className="font-semibold text-blue-800">📊 Audit Logs</span>
                    {showAuditLogs ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                  
                  {showAuditLogs && (
                    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-3 bg-gray-50 border-b flex justify-between items-center flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600">Show last:</label>
                          <select
                            value={auditLogDays}
                            onChange={(e) => setAuditLogDays(parseInt(e.target.value))}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value={1}>1 day</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                          </select>
                        </div>
                        <Button
                          onClick={fetchAuditLogs}
                          disabled={isLoadingLogs}
                          variant="outline"
                          size="sm"
                          className="h-8"
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${isLoadingLogs ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                      
                      {isLoadingLogs ? (
                        <div className="p-6 text-center text-gray-500">
                          <div className="w-6 h-6 border-2 border-gray-300 border-t-tca-blue rounded-full animate-spin mx-auto mb-2"></div>
                          Loading audit logs...
                        </div>
                      ) : auditLogs.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          No audit logs found for this period
                        </div>
                      ) : (
                        <div className="max-h-96 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100 sticky top-0">
                              <tr>
                                <th className="p-2 text-left font-semibold text-gray-700">Timestamp</th>
                                <th className="p-2 text-left font-semibold text-gray-700">Action</th>
                                <th className="p-2 text-left font-semibold text-gray-700">User</th>
                                <th className="p-2 text-center font-semibold text-gray-700">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {auditLogs.map((log, idx) => (
                                <tr key={idx} className={log.success ? '' : 'bg-red-50'}>
                                  <td className="p-2 text-gray-600 whitespace-nowrap">
                                    {formatTimestamp(log.timestamp)}
                                  </td>
                                  <td className="p-2 font-medium text-gray-800">
                                    {log.action?.replace(/_/g, ' ')}
                                  </td>
                                  <td className="p-2 text-gray-600">
                                    {log.username || 'N/A'}
                                  </td>
                                  <td className="p-2 text-center">
                                    {log.success ? (
                                      <span className="text-green-600">✓</span>
                                    ) : (
                                      <span className="text-red-600">✗</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Email Delivery Logs Section */}
                <div className="mb-6">
                  <button
                    data-testid="email-delivery-logs-toggle"
                    onClick={() => {
                      setShowEmailLogs(!showEmailLogs);
                      if (!showEmailLogs && emailLogs.length === 0) {
                        fetchEmailLogs();
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                  >
                    <span className="font-semibold text-orange-800">📧 Client Email Delivery Logs</span>
                    {showEmailLogs ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>

                  {showEmailLogs && (
                    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-3 bg-gray-50 border-b flex justify-between items-center flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600">Show last:</label>
                          <select
                            data-testid="email-logs-days-select"
                            value={emailLogDays}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              setEmailLogDays(val);
                              fetchEmailLogs(val);
                            }}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value={1}>1 day</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                            <option value={90}>90 days</option>
                          </select>
                        </div>
                        <Button
                          data-testid="email-logs-refresh-button"
                          onClick={() => fetchEmailLogs()}
                          disabled={isLoadingEmailLogs}
                          variant="outline"
                          size="sm"
                          className="h-8"
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${isLoadingEmailLogs ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>

                      {isLoadingEmailLogs ? (
                        <div className="p-6 text-center text-gray-500">
                          <div className="w-6 h-6 border-2 border-gray-300 border-t-tca-blue rounded-full animate-spin mx-auto mb-2"></div>
                          Loading email delivery logs...
                        </div>
                      ) : emailLogs.length === 0 ? (
                        <div className="p-6 text-center text-gray-500" data-testid="email-logs-empty-state">
                          No email delivery logs found for this period
                        </div>
                      ) : (
                        <div className="max-h-[32rem] overflow-y-auto">
                          <table className="w-full text-sm" data-testid="email-delivery-logs-table">
                            <thead className="bg-gray-100 sticky top-0">
                              <tr>
                                <th className="p-2 text-left font-semibold text-gray-700">Client Name</th>
                                <th className="p-2 text-left font-semibold text-gray-700">Date</th>
                                <th className="p-2 text-left font-semibold text-gray-700">Email Type</th>
                                <th className="p-2 text-left font-semibold text-gray-700">Recipient(s)</th>
                                <th className="p-2 text-center font-semibold text-gray-700">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {emailLogs.map((log, idx) => (
                                <React.Fragment key={log.id || idx}>
                                  <tr
                                    data-testid={`email-log-client-row-${idx}`}
                                    className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${log.combined_status !== 'success' ? 'bg-red-50' : ''} border-t-2 border-t-gray-300`}
                                  >
                                    <td className="p-2 font-semibold text-gray-800" rowSpan={2}>
                                      {log.client_name || 'N/A'}
                                    </td>
                                    <td className="p-2 text-gray-600 whitespace-nowrap" rowSpan={2}>
                                      {formatTimestamp(log.timestamp)}
                                    </td>
                                    <td className="p-2 text-gray-700">Client Confirmation Email</td>
                                    <td className="p-2 text-gray-600">
                                      {(log.combined_recipients || []).join(', ') || 'N/A'}
                                    </td>
                                    <td className="p-2 text-center" data-testid={`email-log-combined-status-${idx}`}>
                                      {log.combined_status === 'success' ? (
                                        <span className="text-green-600 font-semibold">✓ Sent</span>
                                      ) : (
                                        <span className="text-red-600 font-semibold">✗ Failed</span>
                                      )}
                                    </td>
                                  </tr>
                                  <tr
                                    data-testid={`email-log-admin-row-${idx}`}
                                    className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${log.individual_status !== 'success' ? 'bg-red-50' : ''}`}
                                  >
                                    <td className="p-2 text-gray-700">Internal Admin Email</td>
                                    <td className="p-2 text-gray-600">
                                      {(log.individual_recipients || []).join(', ') || 'N/A'}
                                    </td>
                                    <td className="p-2 text-center" data-testid={`email-log-individual-status-${idx}`}>
                                      {log.individual_status === 'success' ? (
                                        <span className="text-green-600 font-semibold">✓ Sent</span>
                                      ) : (
                                        <span className="text-red-600 font-semibold">✗ Failed</span>
                                      )}
                                    </td>
                                  </tr>
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Footer with TCA Logo */}
      <div className="relative py-4 bg-white border-t">
        <div className="flex justify-center items-center">
          <p className="text-sm text-gray-500">TCA Auditors Admin Dashboard</p>
        </div>
        <img 
          src={TCA_SHORT_LOGO} 
          alt="TCA Logo" 
          className="absolute bottom-2 right-8 h-12 w-auto object-contain opacity-80"
        />
      </div>
    </div>
  );
};

export default AdminPage;
